/**
 * IntakeSummary Component
 * Displays daily nutritional totals (detailed) and a per-meal breakdown.
 *
 * Layout:
 *  Row 1 (primary macros): Energy (kcal + kJ sub-line), Protein, Carbs (sugars sub-line), Fat (sat.FA sub-line)
 *  Row 2 (secondary): Fiber, Sodium
 *  Meal breakdown: Energy, Protein, Carbs, Fat
 */

import { useTranslation } from "@/hooks/useTranslation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import type {
  DailyTotals,
  MealCategoryTotals,
  PrijemKategoria,
} from "../types";
import { getMealCategoryDisplayName } from "../utils";

interface MealCategorySummary {
  kategoria: PrijemKategoria;
  totals: MealCategoryTotals;
}

export interface MacroTargets {
  targetKcal: number;
  proteinG: number;
  fatG: number;
  carbsG: number;
  deficitPercent: number;
  tdee: number;
  bmr: number;
  effectiveDate?: string;
}

interface IntakeSummaryProps {
  dailyTotals: DailyTotals | undefined;
  projectedTotals?: DailyTotals | null;
  mealCategorySummaries?: MealCategorySummary[];
  /** Optional macro targets from the meranie effective for the selected intake date. */
  macroTargets?: MacroTargets | null;
}

type ExtendedTotals = DailyTotals & {
  totalEnergiaKj?: number | string;
  totalCukorG?: number | string;
  totalNmkG?: number | string;
  totalVlakninaG?: number | string;
  totalSodikG?: number | string;
};

/** Safely format a number-or-string value */
function fmt(v: number | string | undefined, decimals = 1): string {
  if (v === undefined || v === null) return "0";
  if (typeof v === "string") {
    const n = parseFloat(v);
    return isNaN(n) ? v : n.toFixed(decimals);
  }
  return v.toFixed(decimals);
}

interface MacroTileProps {
  label: string;
  value: string;
  projected?: string;
  unit?: string;
  colorClass: string;
  size?: "lg" | "sm";
  /** Optional sub-value shown on a second line (e.g. kJ or sugars) */
  subValue?: string;
  subLabel?: string;
  subProjected?: string;
  subUnit?: string;
}

function MacroTile({
  label,
  value,
  projected,
  unit = "g",
  colorClass,
  size = "lg",
  subValue,
  subLabel,
  subProjected,
  subUnit = "g",
}: MacroTileProps) {
  const valClass = size === "lg" ? "text-2xl font-bold" : "text-lg font-bold";
  const isKcal = unit === "kcal";
  const isKj = unit === "kJ";

  // Determine background class based on color class
  let bgClass = "bg-muted";
  if (colorClass.includes("text-primary")) {
    bgClass = "bg-muted";
  } else if (colorClass.includes("text-blue-600")) {
    bgClass = "bg-blue-50";
  } else if (colorClass.includes("text-orange-600")) {
    bgClass = "bg-orange-50";
  } else if (colorClass.includes("text-teal-600")) {
    bgClass = "bg-teal-50";
  } else if (colorClass.includes("text-emerald-600")) {
    bgClass = "bg-emerald-50";
  } else if (colorClass.includes("text-slate-600")) {
    bgClass = "bg-slate-50";
  } else if (colorClass.includes("text-amber-600")) {
    bgClass = "bg-amber-50";
  }

  return (
    <div className={`text-center p-3 rounded-lg ${bgClass}`}>
      <div className={`${valClass} ${colorClass}`}>
        {value}
        <span className="text-sm font-normal ml-0.5">
          {isKcal ? " kcal" : isKj ? " kJ" : unit}
        </span>
      </div>
      {projected !== undefined && (
        <div className={`text-xs font-medium opacity-60 ${colorClass} mt-0.5`}>
          → {projected}
          {isKcal ? " kcal" : isKj ? " kJ" : unit}
        </div>
      )}
      {subValue !== undefined && (
        <div className="text-xs text-muted-foreground mt-0.5">
          {subValue} {subUnit}
          {subLabel && <span className="ml-0.5">{subLabel}</span>}
          {subProjected !== undefined && (
            <span className="opacity-60">
              {" "}
              → {subProjected} {subUnit}
            </span>
          )}
        </div>
      )}
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

/** Render a single progress row: label, progress bar, actual/target */
function TargetRow({
  label,
  actual,
  target,
  unit,
  colorClass,
}: {
  label: string;
  actual: number;
  target: number;
  unit: string;
  colorClass: string;
}) {
  const pct =
    target > 0 ? Math.min(100, Math.round((actual / target) * 100)) : 0;
  const over = actual > target && target > 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className={`font-medium ${colorClass}`}>{label}</span>
        <span className="text-muted-foreground">
          {Math.round(actual)} / {target} {unit}
          {over && (
            <span className="ml-1 text-destructive font-semibold">
              (+{Math.round(actual - target)})
            </span>
          )}
        </span>
      </div>
      <Progress
        value={pct}
        className={`h-2 ${over ? "[&>div]:bg-destructive" : ""}`}
      />
    </div>
  );
}

export function IntakeSummary({
  dailyTotals,
  projectedTotals,
  mealCategorySummaries,
  macroTargets,
}: IntakeSummaryProps) {
  const { t } = useTranslation();

  if (!dailyTotals) return null;

  const isProjected = !!projectedTotals;
  const dt = dailyTotals as ExtendedTotals;
  const pt = projectedTotals as ExtendedTotals | null;

  const hasMicros =
    dt.totalVlakninaG !== undefined || dt.totalSodikG !== undefined;

  return (
    <Card className={`mb-6 ${isProjected ? "border-primary" : ""}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span>{t("intake:summary.dailySummary")}</span>
          {isProjected && (
            <span className="text-sm font-normal text-primary">
              ({t("intake:summary.previewWithNewFood")})
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ── Primary macros ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Energy: kcal main, kJ sub-line */}
          <MacroTile
            label={t("intake:summary.energyKcal")}
            value={fmt(dt.totalEnergiaKcal)}
            projected={pt ? fmt(pt.totalEnergiaKcal) : undefined}
            unit="kcal"
            colorClass="text-amber-600"
            subValue={
              dt.totalEnergiaKj !== undefined
                ? fmt(dt.totalEnergiaKj, 0)
                : undefined
            }
            subUnit="kJ"
            subProjected={
              pt?.totalEnergiaKj !== undefined
                ? fmt(pt.totalEnergiaKj, 0)
                : undefined
            }
          />
          {/* Protein */}
          <MacroTile
            label={t("intake:summary.protein")}
            value={fmt(dt.totalProteinG)}
            projected={pt ? fmt(pt.totalProteinG) : undefined}
            colorClass="text-blue-600"
          />
          {/* Fat: main, saturated FA sub-line */}
          <MacroTile
            label={t("intake:summary.fat")}
            value={fmt(dt.totalTukG)}
            projected={pt ? fmt(pt.totalTukG) : undefined}
            colorClass="text-orange-600"
            subValue={
              dt.totalNmkG !== undefined ? fmt(dt.totalNmkG) : undefined
            }
            subLabel={t("intake:summary.saturatedFatShort", "sat.")}
            subProjected={
              pt?.totalNmkG !== undefined ? fmt(pt.totalNmkG) : undefined
            }
          />
          {/* Carbs: main, sugars sub-line */}
          <MacroTile
            label={t("intake:summary.carbs")}
            value={fmt(dt.totalSacharidG)}
            projected={pt ? fmt(pt.totalSacharidG) : undefined}
            colorClass="text-teal-600"
            subValue={
              dt.totalCukorG !== undefined ? fmt(dt.totalCukorG) : undefined
            }
            subLabel={t("intake:summary.sugars", "sugars")}
            subProjected={
              pt?.totalCukorG !== undefined ? fmt(pt.totalCukorG) : undefined
            }
          />
        </div>

        {/* ── Secondary micronutrients ── */}
        {hasMicros && (
          <div className="grid grid-cols-2 gap-3 pt-2 border-t">
            <MacroTile
              label={t("intake:summary.fiber")}
              value={fmt(dt.totalVlakninaG)}
              projected={pt ? fmt(pt.totalVlakninaG) : undefined}
              colorClass="text-emerald-600"
              size="sm"
            />
            <MacroTile
              label={t("intake:summary.sodium")}
              value={fmt(dt.totalSodikG, 2)}
              projected={pt ? fmt(pt.totalSodikG, 2) : undefined}
              colorClass="text-slate-600"
              size="sm"
            />
          </div>
        )}

        {/* ── Macro targets progress bars ── */}
        {macroTargets && macroTargets.targetKcal > 0 && (
          <div className="pt-2 border-t space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t("intake:summary.macroTargets", "Denné ciele")}
            </h3>
            <TargetRow
              label={t("intake:summary.energyKcal", "kcal")}
              actual={parseFloat(String(dt.totalEnergiaKcal || 0))}
              target={macroTargets.targetKcal}
              unit="kcal"
              colorClass="text-primary"
            />
            <TargetRow
              label={t("intake:summary.protein", "Proteíny")}
              actual={parseFloat(String(dt.totalProteinG || 0))}
              target={macroTargets.proteinG}
              unit="g"
              colorClass="text-blue-600"
            />
            <TargetRow
              label={t("intake:summary.fat", "Tuky")}
              actual={parseFloat(String(dt.totalTukG || 0))}
              target={macroTargets.fatG}
              unit="g"
              colorClass="text-orange-600"
            />
            <TargetRow
              label={t("intake:summary.carbs", "Sacharidy")}
              actual={parseFloat(String(dt.totalSacharidG || 0))}
              target={macroTargets.carbsG}
              unit="g"
              colorClass="text-teal-600"
            />
            <div className="text-xs text-muted-foreground text-right">
              TDEE: {macroTargets.tdee} kcal
              {macroTargets.deficitPercent > 0 && (
                <span className="ml-2">
                  · Deficit: {macroTargets.deficitPercent}%
                </span>
              )}
              {macroTargets.effectiveDate && (
                <span className="ml-2">
                  · {t("intake:summary.targetsEffectiveFrom", "Platné od")}:{" "}
                  {macroTargets.effectiveDate}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Meal breakdown (compact) ── */}
        {mealCategorySummaries && mealCategorySummaries.length > 0 && (
          <div className="pt-2 border-t">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              {t("intake:summary.mealBreakdown")}
            </h3>
            <div className="space-y-1">
              {mealCategorySummaries.map(({ kategoria, totals }) => (
                <div
                  key={kategoria.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/40 text-sm"
                >
                  {/* Category name */}
                  <span className="w-28 shrink-0 font-medium text-primary truncate text-xs">
                    {getMealCategoryDisplayName(
                      kategoria.nazov || kategoria.name || ""
                    )}
                  </span>

                  {/* Macro pills: Energy, Protein, Fat, Carbs */}
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 flex-1 min-w-0">
                    <span className="text-amber-600 font-semibold whitespace-nowrap">
                      {totals.totalEnergiaKcal}
                      <span className="font-normal text-muted-foreground ml-0.5 text-xs">
                        kcal
                      </span>
                    </span>
                    <span className="text-blue-600 whitespace-nowrap">
                      {totals.totalProteinG}
                      <span className="font-normal text-muted-foreground ml-0.5 text-xs">
                        g {t("intake:summary.protein")}
                      </span>
                    </span>
                    <span className="text-orange-600 whitespace-nowrap">
                      {totals.totalTukG}
                      <span className="font-normal text-muted-foreground ml-0.5 text-xs">
                        g {t("intake:summary.fat")}
                      </span>
                    </span>
                    <span className="text-teal-600 whitespace-nowrap">
                      {totals.totalSacharidG}
                      <span className="font-normal text-muted-foreground ml-0.5 text-xs">
                        g {t("intake:summary.carbs")}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
